import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePendingSurveys, type PendingSurveyAssignment } from "@/hooks/usePendingSurveys";
import eabLogo from "@/assets/eab-logo-horizontal.png";

const ORANGE = "#C94F13";

const seenKey = (id: string) => `survey_modal_seen_${id}`;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function formatDueDate(due: string) {
  return new Date(due).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isWithin7Days(due: string) {
  const diff = new Date(due).getTime() - Date.now();
  return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
}

export function SurveyAssignmentModal() {
  const navigate = useNavigate();
  const { pendingAssignments, isLoading } = usePendingSurveys();
  const [open, setOpen] = useState(false);

  const candidate: PendingSurveyAssignment | null = useMemo(() => {
    if (isLoading) return null;
    const now = Date.now();
    const eligible = pendingAssignments.filter((a) => {
      const notExpired = !a.dueAt || new Date(a.dueAt).getTime() > now;
      const notSeen = safeGet(seenKey(a.assignmentId)) !== "true";
      return notExpired && notSeen;
    });
    if (eligible.length === 0) return null;
    // Earliest assigned_at first
    eligible.sort(
      (a, b) => new Date(a.assignedAt).getTime() - new Date(b.assignedAt).getTime(),
    );
    return eligible[0];
  }, [pendingAssignments, isLoading]);

  useEffect(() => {
    if (candidate) setOpen(true);
  }, [candidate]);

  if (!candidate) return null;

  const dueSoon = candidate.dueAt && isWithin7Days(candidate.dueAt);

  const markSeen = () => safeSet(seenKey(candidate.assignmentId), "true");

  const handleTake = () => {
    markSeen();
    setOpen(false);
    navigate(`/survey/${candidate.assignmentId}`);
  };

  const handleLater = () => {
    markSeen();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      modal
      onOpenChange={(next) => {
        // Prevent closing via outside-click / Escape — only buttons close.
        if (next) setOpen(true);
      }}
    >
      <DialogContent
        className="max-w-[480px] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        style={{ fontFamily: "'Public Sans', Arial, sans-serif" }}
      >
        <div className="flex flex-col items-center text-center gap-4 py-2">
          <img src={eabLogo} alt="EAB Connect" className="h-10 object-contain" />

          <h2 className="text-2xl font-bold" style={{ color: ORANGE }}>
            You have a survey waiting
          </h2>

          <div className="space-y-2 w-full">
            <p className="text-lg font-bold text-foreground">{candidate.title}</p>
            {candidate.description && (
              <p className="text-sm text-muted-foreground">{candidate.description}</p>
            )}
            <p className="text-xs text-muted-foreground">Wave: {candidate.waveLabel}</p>
            {candidate.dueAt && (
              <p
                className={`text-sm font-medium ${dueSoon ? "text-red-600" : "text-muted-foreground"}`}
              >
                Due {formatDueDate(candidate.dueAt)}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
            <Button
              onClick={handleTake}
              className="flex-1 text-white hover:opacity-90"
              style={{ backgroundColor: ORANGE }}
            >
              Take Survey Now
            </Button>
            <Button
              onClick={handleLater}
              variant="outline"
              className="flex-1"
              style={{ borderColor: ORANGE, color: ORANGE }}
            >
              Remind Me Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SurveyAssignmentModal;
